(() => {
  const STATE = {
    IN_STOCK: "in-stock",
    LOW: "low",
    SUPPLIER: "supplier",
    MADE: "made",
    SOLDOUT: "soldout"
  };

  const zeroDecimalCurrencies = new Set(["BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF"]);

  const getCurrencyRate = () => {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  };

  // Singular unit when the lead time is exactly "1" (same rule as the Liquid snippets).
  const SINGULAR_WEEK = [["1 weeks", "1 week"], ["1 Wochen", "1 Woche"], ["1 semanas", "1 semana"], ["1 semaines", "1 semaine"], ["1 settimane", "1 settimana"], ["1 weken", "1 week"]];
  const singularWeek = (text, n) => (n === "1" ? SINGULAR_WEEK.reduce((t, [a, b]) => t.split(a).join(b), text) : text);

  const formatMoney = (presentmentCents) => {
    const policy = window.vividifyShippingPolicy;
    if (policy && typeof policy.formatMoney === "function") return policy.formatMoney(presentmentCents);
    const currency = window.Shopify?.currency?.active || "EUR";
    const decimals = zeroDecimalCurrencies.has(currency) ? 0 : 2;
    const amount = Number(presentmentCents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(amount);
    } catch {
      return amount.toFixed(decimals) + " " + currency;
    }
  };

  const getSubscribe = () => {
    try {
      if (typeof subscribe === "function") return subscribe;
    } catch {
      // subscribe is not defined
    }
    return typeof window.subscribe === "function" ? window.subscribe : null;
  };

  const getCartUpdateEventName = () => {
    try {
      if (typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS?.cartUpdate) return PUB_SUB_EVENTS.cartUpdate;
    } catch {
      // PUB_SUB_EVENTS is not defined
    }
    return "cart-update";
  };

  let cartTotalCents = 0;
  const fetchCartTotal = () => {
    const cartUrl = (window.routes?.cart_url || "/cart") + ".js";
    return fetch(cartUrl, { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((cart) => {
        const total = Number(cart?.total_price);
        if (Number.isFinite(total)) cartTotalCents = total;
      })
      .catch(() => {});
  };

  class VividifyStockStatus extends HTMLElement {
    connectedCallback() {
      try {
        const dataElement = this.querySelector("[data-vividify-variants]");
        this.variants = dataElement ? JSON.parse(dataElement.textContent) : [];
      } catch {
        this.variants = [];
      }
      this.variantMap = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.textElement = this.querySelector("[data-vividify-stock-text]");
      this.currentVariant = this.variantMap.get(String(this.dataset.currentVariantId)) || null;

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onQuantityEvent = this.onQuantityEvent.bind(this);
      this.onCounterClick = this.onCounterClick.bind(this);
      this.refreshShipping = this.refreshShipping.bind(this);

      document.addEventListener("variantChangeEnd", this.onVariantChange);
      document.addEventListener("change", this.onQuantityEvent, true);
      document.addEventListener("input", this.onQuantityEvent, true);
      document.addEventListener("click", this.onCounterClick, true);

      const subscribeFn = getSubscribe();
      if (subscribeFn) {
        this.cartUnsubscribe = subscribeFn(getCartUpdateEventName(), () => {
          fetchCartTotal().then(this.refreshShipping);
        });
      }

      this.update(this.currentVariant, false);
      fetchCartTotal().then(this.refreshShipping);
    }

    disconnectedCallback() {
      document.removeEventListener("variantChangeEnd", this.onVariantChange);
      document.removeEventListener("change", this.onQuantityEvent, true);
      document.removeEventListener("input", this.onQuantityEvent, true);
      document.removeEventListener("click", this.onCounterClick, true);
      if (this.cartUnsubscribe) this.cartUnsubscribe();
    }

    onQuantityEvent(event) {
      const target = event.target;
      if (!target || target.name !== "quantity") return;
      this.refreshShipping();
    }

    onCounterClick(event) {
      const target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest(".js-counter-decrease, .js-counter-increase")) return;
      window.setTimeout(this.refreshShipping, 0);
    }

    refreshShipping() {
      this.updateShipping(this.currentVariant);
    }

    getQuantity() {
      const input =
        document.querySelector('form[action*="/cart/add"] input[name="quantity"]') ||
        document.querySelector('quantity-counter input[name="quantity"]');
      const quantity = parseInt(input?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    onVariantChange(event) {
      const source = event.target;
      if (!source || !source.getAttribute) return;
      if (source.getAttribute("data-product-handle") !== this.dataset.productHandle) return;

      const currentVariant = source.currentVariant;
      if (!currentVariant) {
        // Selected option combination does not exist.
        this.currentVariant = null;
        this.update(null, true);
        return;
      }
      const variant = this.variantMap.get(String(currentVariant.id));
      if (!variant) return;
      this.dataset.currentVariantId = String(currentVariant.id);
      this.currentVariant = variant;
      this.update(variant, true);
    }

    update(variant, fromEvent) {
      this.updateShipping(variant);
      this.updateSpecs(variant);
      this.updateDelivery(variant);

      if (!variant) {
        if (fromEvent) this.hidden = true;
        return;
      }
      this.hidden = false;

      let state = STATE.IN_STOCK;
      let message = this.dataset.textInStock || "";

      if (!variant.available) {
        state = STATE.SOLDOUT;
        message = this.dataset.textSoldout || "";
      } else if (variant.managed) {
        if (variant.qty >= 4) {
          state = STATE.IN_STOCK;
          message = this.dataset.textInStock || "";
        } else if (variant.qty >= 1) {
          state = STATE.LOW;
          message = (this.dataset.textLow || "").replace("[qty]", String(variant.qty));
        } else {
          // Available with zero stock: lead time under 4 weeks = brand warehouse, else made to order.
          const lead = Number.isFinite(variant.lead) ? variant.lead : 8;
          // Show the lead time exactly as stored (e.g. "1-2"), same as the card and the server render.
          const leadText = typeof variant.leadText === "string" && variant.leadText !== "" ? variant.leadText : String(lead);
          if (variant.restock) {
            state = STATE.MADE;
            message = (this.dataset.textRestock || "").replace("[date]", String(variant.restock));
          } else if (lead < 4) {
            state = STATE.SUPPLIER;
            message = singularWeek((this.dataset.textSupplier || "").replace("[n]", leadText), leadText);
          } else {
            state = STATE.MADE;
            message = singularWeek((this.dataset.textMade || "").replace("[n]", leadText), leadText);
          }
        }
      }

      this.dataset.state = state;
      if (this.textElement) this.textElement.textContent = message;
    }

    updateShipping(variant) {
      const shipping = document.querySelector("[data-vividify-pdp-shipping]");
      if (!shipping || !variant) return;

      // US market: threshold is already in the local (USD) currency, no EUR conversion.
      const thresholdCents = Number(shipping.dataset.thresholdCents || 25000) * (shipping.dataset.thresholdLocal ? 1 : getCurrencyRate());
      const priceCents = Number(variant.price);
      if (!Number.isFinite(priceCents) || thresholdCents <= 0) return;

      const subtotalCents = priceCents * this.getQuantity() + cartTotalCents;
      const isFree = shipping.dataset.country === "HU" || subtotalCents >= thresholdCents;
      const freeLine = shipping.querySelector("[data-vividify-shipping-free]");
      const paidLine = shipping.querySelector("[data-vividify-shipping-paid]");
      if (freeLine) freeLine.hidden = !isFree;
      if (paidLine) paidLine.hidden = isFree;

      const remainingElement = shipping.querySelector("[data-vividify-shipping-remaining]");
      if (remainingElement) {
        remainingElement.textContent = formatMoney(Math.max(0, thresholdCents - subtotalCents));
      }

      const progress = shipping.querySelector("[data-vividify-shipping-progress]");
      if (progress) {
        progress.style.width = Math.max(0, Math.min(100, (subtotalCents / thresholdCents) * 100)) + "%";
      }
    }

    updateDelivery(variant) {
      const dispatch = document.querySelector("[data-vividify-delivery-dispatch]");
      if (!dispatch || !variant) return;

      let text = dispatch.dataset.textLocal || "";
      if (variant.available && variant.managed && variant.qty <= 0) {
        text = dispatch.dataset.textSupplier || text;
      }
      if (text) dispatch.textContent = text;
    }

    updateSpecs(variant) {
      const table = document.querySelector("[data-vividify-spec-table]");
      if (!table || !variant) return;

      const setRow = (key, value) => {
        const cell = table.querySelector(`[data-vividify-spec="${key}"]`);
        if (!cell) return;
        const row = cell.closest("tr");
        if (value) {
          cell.textContent = value;
          if (row) row.hidden = false;
        } else if (row) {
          row.hidden = true;
        }
      };

      setRow("sku", variant.sku || "");
      setRow("barcode", variant.barcode || "");
      (variant.options || []).forEach((value, index) => {
        const cell = table.querySelector(`[data-vividify-spec-option="${index}"]`);
        if (cell) cell.textContent = value || "";
      });
    }
  }

  if (!customElements.get("vividify-stock-status")) {
    customElements.define("vividify-stock-status", VividifyStockStatus);
  }
})();
